import { useState, type CSSProperties, type ReactNode } from 'react'
import { CssBaseline, ScopedCssBaseline, ThemeProvider, type Theme } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, MemoryRouter, useInRouterContext } from 'react-router-dom'
import { theme as defaultTheme } from './theme'
import { App } from './App'
import { configure, type BasemapUrls } from './api/client'
import { EditionProvider, defaultEdition, type Edition } from './edition'
import { HostProvider, cleanBasePath, type Reference } from './host'
import { setAppVersion, type AppVersion } from './version'

export interface PolygonChecksProps {
  /** The API's address, e.g. `https://api.mars.agribot.ai`. */
  apiUrl: string
  /**
   * The signed-in user's token (from `POST /users`), asked for on every
   * request — so a host can refresh or swap it at will. Null or undefined
   * sends no token.
   */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>
  /** Send cookies with requests. `omit` (the default) for a token; `include` for a session cookie. */
  credentials?: RequestCredentials
  /**
   * Basemap tiles the browser fetches straight from their provider, e.g.
   * `{ light: 'https://…/{z}/{y}/{x}' }`, rather than through the API's tile
   * proxy. Visible to anyone using the page: keyless tiles, or a key your
   * provider restricts to your domains. Styles left out use the proxy.
   */
  basemaps?: BasemapUrls
  /**
   * Where it lives under the host's routes, e.g. `/compliance`. Inside the
   * host's router, its pages are `/compliance/regions/…`; with a router of
   * its own (`router="browser"`), that is the router's basename.
   */
  basePath?: string
  /**
   * Whose router. `auto` (the default): the host's when there is one around
   * it, otherwise one in memory that leaves the address bar alone. `browser`
   * owns the address bar — for an application that is only this.
   */
  router?: 'auto' | 'browser' | 'memory'
  /** Where an in-memory router starts. */
  initialPath?: string
  /** Shown when there is no signed-in caller: the host's own sign-in, or a note. */
  signedOut?: ReactNode
  /** Offered in the account menu as "Sign out". No entry without it. */
  onSignOut?: () => void
  /**
   * Takes the user to the host's own home, such as its dashboard. The mark and
   * the first crumb call it, after asking if work would be lost. Without it they
   * go to the application's regions list.
   */
  onHome?: () => void
  /**
   * Names one of your users, given the API user id you minted for them with
   * POST /users. Agribot holds no names for those users, so without this they
   * show as "User 1a2b3c4d". Used for comment authors, who ran a check, who
   * uploaded a file and the account menu. Return undefined to keep the default.
   */
  userLabel?: (userId: string) => string | undefined | Promise<string | undefined>
  /**
   * Your own references to capture on each upload and plot, e.g.
   * `[{ key: 'contract', label: 'Contract', search: (q) => yourContracts(q) }]`.
   * Stored as given, returned by the API and exported as `ref_<key>`. Keep the
   * array stable (define it outside render), as `search` is called on change.
   */
  references?: Reference[]
  /** What else this build has: an admin section. None by default. */
  edition?: Edition
  /** This build's version, shown at `/version` beside the API's. */
  version?: AppVersion
  /** Share the host's cache; otherwise it keeps its own. */
  queryClient?: QueryClient
  /** Replace the Agribot theme. Most hosts should not. */
  theme?: Theme
  /**
   * The whole page is this: the global CSS reset, and the height of the
   * window. Off, it resets only inside itself and fills its container —
   * give the container a height.
   */
  fullPage?: boolean
  className?: string
  style?: CSSProperties
}

const makeClient = () =>
  new QueryClient({
    defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 10_000 } },
  })

/**
 * The polygon checks application, as a component: regions, uploads, checks,
 * maps, inspection — everything but signing in, which is the host's.
 *
 * One per page: the API address and token are the page's, so two of these
 * would share them.
 */
export function PolygonChecks({
  apiUrl, getToken, credentials = 'omit', basemaps, basePath, router = 'auto', initialPath = '/',
  signedOut, onSignOut, onHome, userLabel, references, edition = defaultEdition, version, queryClient, theme = defaultTheme,
  fullPage = false, className, style,
}: PolygonChecksProps) {
  const [ownClient] = useState(() => queryClient ?? makeClient())
  const inHostRouter = useInRouterContext()
  const mode = router === 'auto' ? (inHostRouter ? 'host' : 'memory') : router
  const base = cleanBasePath(basePath)

  // Before anything below fetches. Cheap and idempotent, so on every render:
  // a host that changes the address or the token source takes effect at once.
  configure({
    baseUrl: apiUrl,
    credentials,
    basemaps,
    getAuthorization: async () => {
      const token = await getToken?.()
      return token ? `Bearer ${token}` : null
    },
  })
  setAppVersion(version)

  const app = (
    <HostProvider value={{ basePath: base, router: mode === 'host' ? 'host' : 'own', signedOut, onSignOut, onHome, userLabel, references }}>
      <EditionProvider value={edition}>
        <App />
      </EditionProvider>
    </HostProvider>
  )
  const routed =
    mode === 'host' ? app
    : mode === 'browser' ? <BrowserRouter basename={base || undefined}>{app}</BrowserRouter>
    : <MemoryRouter initialEntries={[initialPath]}>{app}</MemoryRouter>

  const content = (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient ?? ownClient}>{routed}</QueryClientProvider>
    </ThemeProvider>
  )

  if (fullPage) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {content}
      </ThemeProvider>
    )
  }
  return (
    <ThemeProvider theme={theme}>
      {/* The reset, the fonts and the height stop at this element. */}
      <ScopedCssBaseline
        className={className}
        style={{ height: '100%', position: 'relative', ['--pc-shell-height' as string]: '100%', ...style }}
      >
        {content}
      </ScopedCssBaseline>
    </ThemeProvider>
  )
}
